import React, { useMemo } from "react";
import { HiOutlineChip } from "react-icons/hi";
import { FaAndroid, FaMobileAlt, FaDesktop, FaSignal } from "react-icons/fa";
import { TbArrowsVertical, TbSettings } from "react-icons/tb";
import {
  getDeviceCards,
  getCardTypeLabel,
  getCardModelShortLabel,
  getCardPortLabel,
} from "../../../utils/deviceCards";

/**
 * Bilgi sekmesi → "VERSİYONLAR" — tam genişlikte ayrı satır.
 *
 * Sözleşme: device-info-cards.md (Workspace-#38). Kart listesi her zaman
 * `utils/deviceCards` üzerinden okunur: `deviceInfo.cards` doluysa o, değilse
 * eski düz alanlardan türetilmiş hali (deploy sonrası deviceInfo göndermemiş
 * cihazlar). Yetki kapısı (`devices.infoVersion`) çağıran InfoTab'dadır.
 *
 * Başlıklar CSS `uppercase` ile DEĞİL literal büyük harfle yazılır (Türkçe
 * İ/ı casing'i bozulmasın — docs/rules/ui-components.md).
 *
 * TASARIM (2026-09-21 yenilendi): tablo ve iç kutu YOK. Üstte ekran tarafı
 * (işletim sistemi / uygulama) ikonlu tek satır; altta makinedeki kartlar
 * çerçevesiz bir şerit: her kart tipine göre renkli ikon, ad · konum, model
 * ve sağda belirgin sürüm. Bölmeler yalnız ince ayraçlarla ayrılır.
 */

const Placeholder = () => <span className="text-gray-300">—</span>;

// Kart tipine göre ikon + ton (Tailwind paleti).
const CARD_STYLE = {
  caysetMainBoard: { Icon: HiOutlineChip, tone: "text-emerald-600 bg-emerald-50" },
  fincandaMainBoard: { Icon: HiOutlineChip, tone: "text-violet-600 bg-violet-50" },
  fincandaHanger: { Icon: TbArrowsVertical, tone: "text-amber-600 bg-amber-50" },
  fincandaGrinder: { Icon: TbSettings, tone: "text-sky-600 bg-sky-50" },
  fincandaLcd: { Icon: FaDesktop, tone: "text-indigo-600 bg-indigo-50" },
  fincandaGateway: { Icon: FaSignal, tone: "text-teal-600 bg-teal-50" },
};
const DEFAULT_STYLE = { Icon: HiOutlineChip, tone: "text-gray-500 bg-gray-100" };

// Sürüm: "v" soluk, numara belirgin ve eşit genişlikli rakamlar.
const Version = ({ value, size = "text-lg" }) =>
  value ? (
    <span className={`${size} font-Bold text-gray-900 tabular-nums leading-none`}>
      <span className="text-gray-300 font-Medium mr-0.5">v</span>
      {String(value).replace(/^v/i, "")}
    </span>
  ) : (
    <Placeholder />
  );

// Ekran tarafı (işletim sistemi / uygulama): ikon + etiket + değer, çerçevesiz.
const SystemItem = ({ Icon, label, value, version }) => (
  <div className="flex items-center gap-3 min-w-0">
    <span className="shrink-0 flex h-9 w-9 items-center justify-center rounded-full bg-gray-100 text-gray-500">
      <Icon className="w-4 h-4" />
    </span>
    <div className="min-w-0">
      <div className="text-[11px] font-Medium tracking-wide text-gray-400">{label}</div>
      {version ? (
        <Version value={value} size="text-base" />
      ) : (
        <div className="text-[13px] font-SemiBold text-gray-900 truncate" title={value || ""}>
          {value || <Placeholder />}
        </div>
      )}
    </div>
  </div>
);

// Makinedeki tek kart — kutu değil, şeridin bir bölmesi.
const CardSegment = ({ card }) => {
  const { Icon, tone } = CARD_STYLE[card.type] || DEFAULT_STYLE;
  const port = getCardPortLabel(card);
  const model = getCardModelShortLabel(card.model);
  return (
    <div className="flex items-center gap-3 px-4 py-3 min-w-0">
      <span className={`shrink-0 flex h-10 w-10 items-center justify-center rounded-xl ${tone}`}>
        <Icon className="w-5 h-5" />
      </span>
      <div className="min-w-0 flex-1">
        <div className="text-[13px] font-SemiBold text-gray-900 truncate">
          {getCardTypeLabel(card.type)}
          {port !== "–" && <span className="font-Medium text-gray-400"> · {port}</span>}
        </div>
        <div className={`text-xs truncate ${model ? "text-gray-500" : "text-gray-400"}`}>
          {model ? `Model ${model}` : "Model bilinmiyor"}
        </div>
      </div>
      <div className="shrink-0">
        <Version value={card.version} />
      </div>
    </div>
  );
};

const DeviceVersionsCard = ({ deviceInfo }) => {
  const cards = useMemo(() => getDeviceCards(deviceInfo), [deviceInfo]);
  // Eski kart seti (OKC, hwVersion "OKC63"): tablet yok; işletim sistemi /
  // uygulama 4G'nin sürümü — sürümler kart listesinden (LCD, 4G) okunur (WS#402).
  const isOkc = /^OKC\d+$/.test(String(deviceInfo?.hwVersion ?? ""));

  return (
    <div className="rounded-xl bg-white border border-gray-200 shadow-sm sm:hover:shadow-md transition-shadow overflow-hidden">
      <div className="flex items-center gap-2.5 px-4 py-2.5 bg-gray-50/80 border-b border-gray-100">
        <div className="bg-violet-500 text-white p-1.5 rounded-lg shadow-sm">
          <HiOutlineChip className="w-4 h-4" />
        </div>
        <span className="text-sm font-SemiBold text-gray-800">VERSİYONLAR</span>
        {cards.length > 0 && (
          <span className="ml-auto text-xs text-gray-400">{cards.length} kart</span>
        )}
      </div>

      {/* Ekran: işletim sistemi + uygulama, tek satır */}
      {!isOkc && (
        <div className="flex flex-wrap items-center gap-x-10 gap-y-3 px-4 py-3">
          <SystemItem Icon={FaAndroid} label="İŞLETİM SİSTEMİ" value={deviceInfo?.osVersion} />
          <SystemItem Icon={FaMobileAlt} label="UYGULAMA" value={deviceInfo?.appVersion} version />
        </div>
      )}

      {/* Kartlar: çerçevesiz şerit — geniş ekranda yan yana bölmeler (dikey
          ayraç), telefonda alt alta (yatay ayraç). İç içe kutu YOK. */}
      {cards.length > 0 ? (
        // Her bölmenin sağ ve alt ayracı var; -mr-px/-mb-px dış kenardakileri
        // kartın overflow-hidden sınırının dışına iter — satır sayısından
        // bağımsız temiz ızgara çizgileri.
        <div className="border-t border-gray-100">
          <div className="-mr-px -mb-px grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3">
            {cards.map((card) => (
              <div
                key={`${card.type}:${card.port}`}
                className="border-b border-r border-gray-100 hover:bg-gray-50/60 transition-colors"
              >
                <CardSegment card={card} />
              </div>
            ))}
          </div>
        </div>
      ) : (
        <p className="border-t border-gray-100 px-4 py-3 text-xs text-gray-400 italic">
          Kart sürümü bildirilmemiş
        </p>
      )}
    </div>
  );
};

export default DeviceVersionsCard;
